import {
  ClearOutlined,
  FilterOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  Button,
  DatePicker,
  DatePickerProps,
  Form,
  Input,
  Modal,
  Row,
  Select,
} from 'antd';
import BottomSheet from 'components/UI/Responsive/BottomSheet';
import FilterChips, {
  FilterChip,
} from 'components/UI/Responsive/FilterChips';
import * as BuyIntentionModels from 'model/BuyIntention';
import moment, { Moment } from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  isCardsLayout,
  useResponsiveLayout,
} from 'hooks/layout/useResponsiveLayout';
import { useDispatch, useSelector } from 'react-redux';
import * as BuyIntentionCalls from 'store/state/buyIntention/apiCalls';
import * as BuyIntentionSelectors from 'store/state/buyIntention/buyIntenteionSelectors';
import * as sessionSelectors from 'store/state/session/sessionSelectors';
import {
  alertActions,
  buyIntentionActions,
  sessionActions,
} from 'store/state/slices';
import { VerticalFormFields } from 'styles/VerticalFormFields';
// eslint-disable-next-line import/no-extraneous-dependencies
import * as S from './style';

const { Option } = Select;
const UNAUTHORIZED_REQUEST = 'UNAUTHORIZED_REQUEST';

function prepareDate(date: Moment) {
  return date.set('hour', 0).format('YYYY-MM-DD');
}

export default function FormBuyIntention() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const customer = useSelector(BuyIntentionSelectors.getIntentionCustomer);
  const customers = useSelector(sessionSelectors.getCustomers);
  const assembledItems = useSelector(BuyIntentionSelectors.getBuyIntentionEdit);
  const currentStep = useSelector(BuyIntentionSelectors.getCurrentStep);
  const producers = useSelector(BuyIntentionSelectors.getProducers);
  const layout = useResponsiveLayout();
  const isMobile = isCardsLayout(layout);
  const isPhone = layout === 'phone';

  const [formBuyIntention] = Form.useForm();
  const [deliveryDate, setDeliveryDate] = useState<string | undefined>(
    undefined,
  );
  const [comercialDate, setComercialDate] = useState<string | undefined>(
    undefined,
  );
  const [product, setProduct] = useState<string | undefined>(undefined);
  const [producerId, setProducerId] = useState<number | undefined>(undefined);
  const [isFilterSheetOpen, setFilterSheetOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<
    Partial<Pick<BuyIntentionModels.FormFilters, 'product' | 'producer'>>
  >({});

  const onChangeDeliveryDate: DatePickerProps['onChange'] = (dateString) => {
    const date = moment(dateString).format('YYYY-MM-DD');
    const dateFinish = moment(dateString).format('YYYY-MM-DDTHH:mm:ss.SSSS');
    setComercialDate(undefined);
    formBuyIntention.setFieldsValue({ comercialDate: null });
    if (dateString !== null) {
      setDeliveryDate(date);
      dispatch(buyIntentionActions.setDeliveryDateFinish(dateFinish));
    } else {
      setDeliveryDate(undefined);
      dispatch(buyIntentionActions.setDeliveryDateFinish(''));
      dispatch(buyIntentionActions.setComercialDateFinish(''));
    }
  };

  const onChangeComercialDate: DatePickerProps['onChange'] = (dateString) => {
    const date = moment(dateString).format('YYYY-MM-DD');
    const dateFinish = moment(dateString).format('YYYY-MM-DDTHH:mm:ss.SSSS');
    if (dateString !== null) {
      setComercialDate(date);
      dispatch(buyIntentionActions.setComercialDateFinish(dateFinish));
    } else {
      setComercialDate(undefined);
      dispatch(buyIntentionActions.setComercialDateFinish(''));
    }
  };

  useEffect(() => {
    formBuyIntention.setFieldsValue({ customer: customer?.id });
  }, [formBuyIntention, customer?.id]);

  const onChangeCustomer = (customerId: number | undefined) => {
    const selected = customers.find(({ id }) => id === customerId);
    const applyCustomer = () => {
      dispatch(buyIntentionActions.setClearAssembledOrder());
      dispatch(buyIntentionActions.setIntentionCustomer(selected));
    };

    if (!Array.isArray(assembledItems) || assembledItems.length === 0) {
      applyCustomer();
      return;
    }
    Modal.confirm({
      title: 'Trocar a chave de cliente?',
      content:
        'Os produtos buscados e os valores informados serão descartados.',
      okText: 'Trocar',
      cancelText: 'Cancelar',
      onOk: applyCustomer,
      onCancel: () =>
        formBuyIntention.setFieldsValue({ customer: customer?.id }),
    });
  };

  const onSubmit = useCallback(
    (formValues: BuyIntentionModels.FormFilters) => {
      const delivery = formBuyIntention.getFieldValue('deliveryDate');
      const comercial = formBuyIntention.getFieldValue('comercialDate');
      // if (!customer?.accountCode) {
      //   dispatch(errorActions.throw(UNAUTHORIZED_REQUEST));
      //   return;
      // }
      dispatch(
        sessionActions.setDates({
          billingDate: comercial ? prepareDate(comercial) : '',
          deliveryDate: delivery ? prepareDate(delivery) : '',
        }),
      );
      dispatch(
        buyIntentionActions.setDatesForOffers({
          comercial: comercial ? prepareDate(comercial) : '',
          delivery: delivery ? prepareDate(delivery) : '',
        }),
      );
      if (formValues.producer) {
        dispatch(
          BuyIntentionCalls.fetchOffersByProducers({
            producerId: formValues?.producer,
            keyword: formValues.product,
            accountCode: customer?.accountCode,
          }),
        );
        dispatch(buyIntentionActions.setIsLoading(true));
        dispatch(buyIntentionActions.setSClearTotalPriceOffers());
        setAppliedFilters({
          product: formValues.product,
          producer: formValues.producer,
        });
      } else if (formValues.product) {
        dispatch(
          BuyIntentionCalls.fetchOffersByProducts({
            keyword: formValues?.product,
            accountCode: customer?.accountCode,
          }),
        );
        dispatch(buyIntentionActions.setIsLoading(true));
        dispatch(buyIntentionActions.setSClearTotalPriceOffers());
        setAppliedFilters({ product: formValues.product });
      } else {
        dispatch(
          alertActions.alertRequest({
            message: 'Preencha o campo Produto ou Produtor!',
            type: 'warning',
            showIcon: true,
            timeout: 3000,
          }),
        );
      }
    },
    [formBuyIntention, dispatch, customer?.accountCode],
  );

  const disableDatesDelivery = (value: Moment) => {
    const today = moment().startOf('day');
    const day = value.date();
    const month = value.month();
    return (
      value.isBefore(today) ||
      (month === 11 && day === 25) ||
      (month === 0 && day === 1)
    );
  };

  const disableDatesComercial = (value: Moment) => {
    const today = moment().startOf('day');
    const day = value.date();
    const month = value.month();
    if (deliveryDate !== undefined) {
      let lastDay: any;
      const initalDay = moment(deliveryDate).startOf('day');
      if (moment(deliveryDate).format('dddd') === 'sábado') {
        lastDay = moment(deliveryDate).add(3, 'days').startOf('day');
      } else {
        lastDay = moment(deliveryDate).add(2, 'days').startOf('day');
      }

      return (
        value.isAfter(lastDay) ||
        value.isBefore(initalDay) ||
        (month === 11 && day === 25) ||
        (month === 0 && day === 1)
      );
    }
    return value.isBefore(today);
  };

  const handleKeyDown = (event: any) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      formBuyIntention.submit();
      setFilterSheetOpen(false);
    }
  };

  const searchFromSheet = () => {
    formBuyIntention.submit();
    setFilterSheetOpen(false);
  };

  const clearSheetFilters = () => {
    setProducerId(undefined);
    setProduct(undefined);
    formBuyIntention.setFieldsValue({
      product: undefined,
      producer: undefined,
    });
  };

  const appliedProducer = Array.isArray(producers)
    ? producers.find(
        (value: BuyIntentionModels.Producers) =>
          value.id === appliedFilters.producer,
      )
    : undefined;

  const appliedChips: FilterChip[] = [];
  if (appliedFilters.product) {
    appliedChips.push({
      key: 'product',
      name: t('buyIntention.form.product'),
      text: appliedFilters.product,
    });
  }
  if (appliedProducer) {
    appliedChips.push({
      key: 'producer',
      name: t('buyIntention.form.producer'),
      text: `${appliedProducer.account} - ${appliedProducer.tradingName}`,
      highlighted: true,
    });
  }

  const clearForm = () => {
    setComercialDate(undefined);
    setDeliveryDate(undefined);
    setProducerId(undefined);
    setProduct(undefined);
    formBuyIntention.resetFields();
    formBuyIntention.setFieldsValue({ customer: customer?.id });
  };

  function getPopupContainer(triggerNode: any) {
    return triggerNode.parentElement;
  }

  const productField = (
    <Form.Item label={t('buyIntention.form.product')} name="product">
      <Input
        placeholder="Pesquise aqui"
        inputMode="search"
        value={product}
        style={{
          width: isMobile ? '100%' : 300,
        }}
        onChange={(v) => setProduct(v.target.value)}
        onKeyDown={handleKeyDown}
      />
    </Form.Item>
  );

  const producerField = (
    <Form.Item label={t('buyIntention.form.producer')} name="producer">
      <Select
        getPopupContainer={(v) => getPopupContainer(v)}
        style={{ width: isMobile ? '100%' : 330 }}
        onChange={setProducerId}
        value={producerId}
        allowClear
        showSearch
        optionFilterProp="children"
        filterOption
        placeholder="Selecione"
        onKeyDown={handleKeyDown}
        dropdownStyle={{ minWidth: isMobile ? '100%' : '490px' }}
      >
        {Array.isArray(producers) &&
          producers.map((value: BuyIntentionModels.Producers) => (
            <Option key={value.id} value={value.id}>
              {value.account}-{value.corporateName}({value.tradingName})
            </Option>
          ))}
      </Select>
    </Form.Item>
  );

  return (
    <S.Step form={formBuyIntention} key="1">
      <Form
        layout={isMobile ? 'vertical' : 'horizontal'}
        form={formBuyIntention}
        onFinish={onSubmit}
      >
        <Row gutter={8}>
          <S.ColStyled isMobile={isMobile} xs={24} md={12} lg={12}>
            <Form.Item
              label={t('buyIntention.form.customer')}
              name="customer"
              rules={[
                {
                  required: true,
                  message: 'Selecione a chave de cliente.',
                },
              ]}
            >
              <Select
                getPopupContainer={(v) => getPopupContainer(v)}
                disabled={currentStep !== 0}
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Selecione"
                onChange={onChangeCustomer}
                options={customers.map(({ id, accountCode, name }) => ({
                  value: id,
                  label: `${accountCode} - ${name}`,
                }))}
              />
            </Form.Item>
          </S.ColStyled>

          <S.ColStyled isMobile={isMobile} xs={24} md={6} lg={6}>
            <Form.Item
              key="form-item-delivery-date"
              label={t('buyIntention.form.deliveryDate')}
              name="deliveryDate"
              rules={[
                {
                  required: true,
                  message: 'Selecione a data.',
                },
              ]}
            >
              <DatePicker
                getPopupContainer={(v) => getPopupContainer(v)}
                allowClear
                inputReadOnly={isMobile}
                disabledDate={disableDatesDelivery}
                format="DD/MM/YYYY"
                placeholder="Selecione"
                onChange={onChangeDeliveryDate}
              />
            </Form.Item>
          </S.ColStyled>

          <S.ColStyled isMobile={isMobile} xs={24} md={6} lg={6}>
            <Form.Item
              key="form-item-comercial-date"
              label={t('buyIntention.form.comercialDate')}
              name="comercialDate"
              rules={[
                {
                  required: true,
                  message: 'Selecione a data.',
                },
              ]}
            >
              <DatePicker
                getPopupContainer={(v) => getPopupContainer(v)}
                allowClear
                inputReadOnly={isMobile}
                disabledDate={disableDatesComercial}
                disabled={!deliveryDate}
                format="DD/MM/YYYY"
                placeholder="Selecione"
                onChange={onChangeComercialDate}
              />
            </Form.Item>
          </S.ColStyled>
        </Row>

        {!isMobile && (
          <S.ContentTitleProducers stepTwo={currentStep === 1}>
            <S.Line lineSize={3} />
            <S.TitleProducers>
              {t('buyIntention.form.productsLine')}
            </S.TitleProducers>
            <S.Line lineSize={100} />
          </S.ContentTitleProducers>
        )}

        {currentStep === 0 && isPhone && (
          <>
            <S.PhoneFilterBar>
              {appliedChips.length > 0 ? (
                <FilterChips chips={appliedChips} />
              ) : (
                <S.PhoneFilterEmpty>
                  Para selecionar o produtor e o produto desejado, clique no
                  botão &quot;Filtros&quot; ao lado.
                </S.PhoneFilterEmpty>
              )}
              <S.PhoneFilterButton
                icon={<FilterOutlined aria-hidden />}
                onClick={() => setFilterSheetOpen(true)}
              >
                Filtros
              </S.PhoneFilterButton>
            </S.PhoneFilterBar>

            <BottomSheet
              isOpen={isFilterSheetOpen}
              onClose={() => setFilterSheetOpen(false)}
              title="Filtros"
              keepMounted
              footer={
                <S.SheetActions>
                  <Button onClick={clearSheetFilters}>Limpar</Button>
                  <Button type="primary" onClick={searchFromSheet}>
                    Buscar
                  </Button>
                </S.SheetActions>
              }
            >
              <VerticalFormFields>
                <S.ColStyled isMobile xs={24}>
                  {productField}
                  {producerField}
                </S.ColStyled>
              </VerticalFormFields>
            </BottomSheet>

            <S.MobileHint>
              Para fazer alguma interação com a tabela, clique na oferta.
            </S.MobileHint>
          </>
        )}

        {currentStep === 0 && !isPhone && (
          <S.RowStyled justify="space-between">
            <S.ColStyled
              isMobile={isMobile}
              xs={24}
              md={8}
              lg={8}
              style={{ zIndex: 10 }}
            >
              {productField}
            </S.ColStyled>

            <S.ContentAside isMobile={isMobile}>
              <S.ColStyled isMobile={isMobile} xs={24} md={8} lg={8}>
                {producerField}
              </S.ColStyled>

              {!isMobile && (
                <>
                  <S.ContentButton>
                    <Button aria-label="Limpar" onClick={clearForm}>
                      <ClearOutlined aria-hidden />
                    </Button>
                  </S.ContentButton>

                  <S.ContentButton>
                    <Button
                      aria-label="Buscar"
                      type="primary"
                      onClick={() => formBuyIntention.submit()}
                    >
                      <SearchOutlined aria-hidden />
                    </Button>
                  </S.ContentButton>
                </>
              )}
            </S.ContentAside>

            {isMobile && (
              <S.ContentMobileButton>
                <S.ContentButton isMobile={isMobile}>
                  <Button aria-label="Limpar" onClick={clearForm}>
                    <ClearOutlined aria-hidden />
                  </Button>
                </S.ContentButton>

                <S.ContentButton isMobile={isMobile}>
                  <Button
                    aria-label="Buscar"
                    type="primary"
                    onClick={() => formBuyIntention.submit()}
                  >
                    <SearchOutlined aria-hidden />
                  </Button>
                </S.ContentButton>
              </S.ContentMobileButton>
            )}

            {isMobile && (
              <S.MobileHint>
                Para fazer alguma interação com a tabela, clique na oferta.
              </S.MobileHint>
            )}
          </S.RowStyled>
        )}

        {currentStep === 0 && isMobile && <S.MobileDivider />}
      </Form>
    </S.Step>
  );
}
